import { getTokenConfig, getTokenConfigBySymbol } from './tokenUtils';
export const getActionColor = (transactionType, customColors) => {
    const defaultColors = {
        Deposit: 'bg-[#48C491]',
        CollectRewards: 'bg-[#48C491]',
        Withdraw: 'bg-[#F6921B]',
        Repay: 'bg-[#F6921B]',
        Borrow: 'bg-[#FFD1A7]',
        Liquidation: 'bg-[#FD7373]',
    };
    const colors = customColors || defaultColors;
    return colors[transactionType] || 'bg-gray-500';
};
export const getDefaultFilterOptions = () => [
    { label: 'All', value: 'ALL' },
    { label: 'Deposit', value: 'Deposit' },
    { label: 'Withdraw', value: 'Withdraw' },
    { label: 'Borrow', value: 'Borrow' },
    { label: 'Repay', value: 'Repay' },
    { label: 'Claim Rewards', value: 'CollectRewards' },
    { label: 'Liquidation', value: 'Liquidation' },
];
/**
 * Transform raw backend transaction data in a format that the UI can use
 */
export function transformBackendTransaction(rawTx) {
    var _a, _b, _c;
    const summaries = [];
    // Calculate total re-deposited amounts by token type
    const reDepositedTotals = new Map();
    if (rawTx.summary.claimed_and_resupplied && rawTx.summary.claimed_and_resupplied.length > 0) {
        rawTx.summary.claimed_and_resupplied.forEach((item) => {
            const tokenType = item.claimed.token_type;
            const amount = BigInt(item.claimed.amount);
            reDepositedTotals.set(tokenType, (reDepositedTotals.get(tokenType) || BigInt(0)) + amount);
        });
    }
    // First pass: collect all deposits by token type to check if they're re-deposited rewards
    const depositsByToken = new Map();
    const mainActions = Array.isArray(rawTx.summary.main_action)
        ? rawTx.summary.main_action
        : [rawTx.summary.main_action];
    for (const action of mainActions) {
        const actions = Array.isArray(action) ? action : [action];
        actions.forEach((a) => {
            if (a.action_type === 'Deposit') {
                if (!depositsByToken.has(a.coin_type)) {
                    depositsByToken.set(a.coin_type, []);
                }
                depositsByToken.get(a.coin_type).push(a);
            }
        });
    }
    // Determine which deposits are actually re-deposited rewards
    const depositsToSkip = new Set();
    depositsByToken.forEach((deposits, coinType) => {
        const totalDeposited = deposits.reduce((sum, d) => sum + BigInt(d.amount), BigInt(0));
        const reDepositedTotal = reDepositedTotals.get(coinType);
        // If the total deposits for this token match the re-deposited amount, skip all of them
        if (reDepositedTotal && totalDeposited === reDepositedTotal) {
            deposits.forEach((d) => depositsToSkip.add(d));
        }
    });
    // Process actions, skipping deposits that are re-deposited rewards
    for (const action of mainActions) {
        if (Array.isArray(action)) {
            action.forEach((a) => processTxnType(a, summaries, rawTx.timestamp, depositsToSkip));
        }
        else {
            processTxnType(action, summaries, rawTx.timestamp, depositsToSkip);
        }
    }
    // Add CollectRewards summary from claimed_and_resupplied
    if ((rawTx.summary.claimed_and_resupplied && rawTx.summary.claimed_and_resupplied.length > 0) ||
        (rawTx.summary.rewards_claimed && rawTx.summary.rewards_claimed.length > 0)) {
        summaries.push(createCollectRewardsSummaryFromSummary(rawTx.summary, rawTx.timestamp));
    }
    // Handle liquidation details
    if (rawTx.summary.liquidation_details) {
        summaries.push(createLiquidationSummaryFromSummary(rawTx.summary.liquidation_details, rawTx.timestamp));
    }
    // Transform closing_net_worth_tokens from Record<string, string> to TokenAmount[]
    let closingNetWorthTokens;
    if ((_a = rawTx.summary) === null || _a === void 0 ? void 0 : _a.closing_net_worth_tokens) {
        closingNetWorthTokens = Object.entries(rawTx.summary.closing_net_worth_tokens).map(([symbol, amountRaw]) => {
            return {
                symbol,
                coinType: symbol, // Store symbol as coinType - formatAmount and getTokenConfig will handle it
                amountRaw: String(amountRaw),
                amountFormatted: formatAmount(String(amountRaw), symbol),
            };
        });
    }
    return {
        id: rawTx.digest,
        positionId: rawTx.position_id || '',
        digest: rawTx.digest,
        timestamp: rawTx.timestamp,
        summaries,
        types: summaries.map((s) => s.action),
        isPTB: summaries.length > 1,
        closingNetWorthTokens,
        closingNetWorthUsd: (_b = rawTx.summary) === null || _b === void 0 ? void 0 : _b.closing_net_worth_usd,
        closingHealthFactor: (_c = rawTx.summary) === null || _c === void 0 ? void 0 : _c.closing_health_factor,
    };
}
// =================================
// Summary Processing Functions
// =================================
function processTxnType(action, summaries, timestamp, depositsToSkip) {
    switch (action.action_type) {
        case 'Deposit':
            addOrMergeDepositSummary(summaries, action, timestamp, depositsToSkip);
            break;
        case 'Withdraw':
            addOrMergeWithdrawSummary(summaries, action, timestamp);
            break;
        case 'Borrow':
            addOrMergeBorrowSummary(summaries, action, timestamp);
            break;
        case 'Repay':
            addOrMergeRepaySummary(summaries, action, timestamp);
            break;
        // CollectRewards handled separately
    }
}
function addOrMergeDepositSummary(summaries, action, timestamp, depositsToSkip) {
    if (depositsToSkip.has(action)) {
        return; // Skip this deposit, it's already shown in CollectRewards
    }
    const existing = summaries.find((s) => s.action === 'Deposit');
    if (existing) {
        // Check if we already have this token type
        const existingToken = existing.tokens.find((t) => t.coinType === action.coin_type);
        if (existingToken) {
            // Aggregate amounts
            const newAmount = BigInt(existingToken.amountRaw) + BigInt(action.amount);
            existingToken.amountRaw = newAmount.toString();
            existingToken.amountFormatted = formatAmount(newAmount.toString(), action.coin_type);
            if (existingToken.valueUsd && action.amount_usd) {
                existingToken.valueUsd += Number.parseFloat(action.amount_usd) / 1000000;
            }
            else if (action.amount_usd) {
                existingToken.valueUsd = Number.parseFloat(action.amount_usd) / 1000000;
            }
        }
        else {
            existing.tokens.push({
                symbol: extractTokenSymbol(action.coin_type),
                amountRaw: action.amount,
                coinType: action.coin_type,
                amountFormatted: formatAmount(action.amount, action.coin_type),
                valueUsd: action.amount_usd ? Number.parseFloat(action.amount_usd) / 1000000 : undefined,
            });
        }
        existing.markets.push({ marketId: Number(action.market_id) });
    }
    else {
        summaries.push({
            action: 'Deposit',
            tokens: [
                {
                    symbol: extractTokenSymbol(action.coin_type),
                    amountRaw: action.amount,
                    coinType: action.coin_type,
                    amountFormatted: formatAmount(action.amount, action.coin_type),
                    valueUsd: action.amount_usd
                        ? Number.parseFloat(action.amount_usd) / 1000000
                        : undefined,
                },
            ],
            markets: [{ marketId: Number(action.market_id) }],
            timestamp,
        });
    }
}
function addOrMergeWithdrawSummary(summaries, action, timestamp) {
    const existing = summaries.find((s) => s.action === 'Withdraw');
    if (existing) {
        // Check if we already have this token type
        const existingToken = existing.tokens.find((t) => t.coinType === action.coin_type);
        if (existingToken) {
            // Aggregate amounts
            const newAmount = BigInt(existingToken.amountRaw) + BigInt(action.amount);
            existingToken.amountRaw = newAmount.toString();
            existingToken.amountFormatted = formatAmount(newAmount.toString(), action.coin_type);
            if (existingToken.valueUsd && action.amount_usd) {
                existingToken.valueUsd += Number.parseFloat(action.amount_usd) / 1000000;
            }
            else if (action.amount_usd) {
                existingToken.valueUsd = Number.parseFloat(action.amount_usd) / 1000000;
            }
        }
        else {
            existing.tokens.push({
                symbol: extractTokenSymbol(action.coin_type),
                amountRaw: action.amount,
                coinType: action.coin_type,
                amountFormatted: formatAmount(action.amount, action.coin_type),
                valueUsd: action.amount_usd ? Number.parseFloat(action.amount_usd) / 1000000 : undefined,
            });
        }
        existing.markets.push({ marketId: Number(action.market_id) });
    }
    else {
        summaries.push({
            action: 'Withdraw',
            tokens: [
                {
                    symbol: extractTokenSymbol(action.coin_type),
                    amountRaw: action.amount,
                    coinType: action.coin_type,
                    amountFormatted: formatAmount(action.amount, action.coin_type),
                    valueUsd: action.amount_usd
                        ? Number.parseFloat(action.amount_usd) / 1000000
                        : undefined,
                },
            ],
            markets: [{ marketId: Number(action.market_id) }],
            timestamp,
        });
    }
}
function addOrMergeBorrowSummary(summaries, action, timestamp) {
    const existing = summaries.find((s) => s.action === 'Borrow');
    if (existing) {
        // Check if we already have this token type
        const existingToken = existing.tokens.find((t) => t.coinType === action.coin_type);
        if (existingToken) {
            // Aggregate amounts
            const newAmount = BigInt(existingToken.amountRaw) + BigInt(action.amount);
            existingToken.amountRaw = newAmount.toString();
            existingToken.amountFormatted = formatAmount(newAmount.toString(), action.coin_type);
            if (existingToken.valueUsd && action.amount_usd) {
                existingToken.valueUsd += Number.parseFloat(action.amount_usd) / 1000000;
            }
            else if (action.amount_usd) {
                existingToken.valueUsd = Number.parseFloat(action.amount_usd) / 1000000;
            }
        }
        else {
            existing.tokens.push({
                symbol: extractTokenSymbol(action.coin_type),
                amountRaw: action.amount,
                coinType: action.coin_type,
                amountFormatted: formatAmount(action.amount, action.coin_type),
                valueUsd: action.amount_usd ? Number.parseFloat(action.amount_usd) / 1000000 : undefined,
            });
        }
        existing.markets.push({ marketId: Number(action.market_id) });
    }
    else {
        summaries.push({
            action: 'Borrow',
            tokens: [
                {
                    symbol: extractTokenSymbol(action.coin_type),
                    amountRaw: action.amount,
                    coinType: action.coin_type,
                    amountFormatted: formatAmount(action.amount, action.coin_type),
                    valueUsd: action.amount_usd
                        ? Number.parseFloat(action.amount_usd) / 1000000
                        : undefined,
                },
            ],
            markets: [{ marketId: Number(action.market_id) }],
            timestamp,
        });
    }
}
function addOrMergeRepaySummary(summaries, action, timestamp) {
    const existing = summaries.find((s) => s.action === 'Repay');
    if (existing) {
        // Check if we already have this token type
        const existingToken = existing.tokens.find((t) => t.coinType === action.coin_type);
        if (existingToken) {
            // Aggregate amounts
            const newAmount = BigInt(existingToken.amountRaw) + BigInt(action.amount);
            existingToken.amountRaw = newAmount.toString();
            existingToken.amountFormatted = formatAmount(newAmount.toString(), action.coin_type);
            if (existingToken.valueUsd && action.amount_usd) {
                existingToken.valueUsd += Number.parseFloat(action.amount_usd) / 1000000;
            }
            else if (action.amount_usd) {
                existingToken.valueUsd = Number.parseFloat(action.amount_usd) / 1000000;
            }
        }
        else {
            existing.tokens.push({
                symbol: extractTokenSymbol(action.coin_type),
                amountRaw: action.amount,
                coinType: action.coin_type,
                amountFormatted: formatAmount(action.amount, action.coin_type),
                valueUsd: action.amount_usd ? Number.parseFloat(action.amount_usd) / 1000000 : undefined,
            });
        }
        existing.markets.push({ marketId: Number(action.market_id) });
    }
    else {
        summaries.push({
            action: 'Repay',
            tokens: [
                {
                    symbol: extractTokenSymbol(action.coin_type),
                    amountRaw: action.amount,
                    coinType: action.coin_type,
                    amountFormatted: formatAmount(action.amount, action.coin_type),
                    valueUsd: action.amount_usd
                        ? Number.parseFloat(action.amount_usd) / 1000000
                        : undefined,
                },
            ],
            markets: [{ marketId: Number(action.market_id) }],
            timestamp,
        });
    }
}
function createCollectRewardsSummaryFromSummary(summary, timestamp) {
    var _a, _b;
    // claimed_and_resupplied = rewards that were re-deposited
    const depositedRewards = ((_a = summary.claimed_and_resupplied) === null || _a === void 0 ? void 0 : _a.map((item) => ({
        symbol: extractTokenSymbol(item.claimed.token_type),
        amountRaw: item.claimed.amount,
        coinType: item.claimed.token_type,
        amountFormatted: formatAmount(item.claimed.amount, item.claimed.token_type),
    }))) || [];
    // rewards_claimed = rewards sent to wallet (NOT re-deposited)
    const walletRewards = ((_b = summary.rewards_claimed) === null || _b === void 0 ? void 0 : _b.map((item) => ({
        symbol: extractTokenSymbol(item.coin_type),
        amountRaw: item.amount,
        coinType: item.coin_type,
        amountFormatted: formatAmount(item.amount, item.coin_type),
    }))) || [];
    // Total rewards = deposited + wallet
    const allRewards = [...depositedRewards, ...walletRewards];
    return {
        action: 'CollectRewards',
        rewards: allRewards,
        deposited: depositedRewards.length > 0 ? depositedRewards : undefined,
        locked: walletRewards.length > 0 ? walletRewards : undefined,
        timestamp,
    };
}
function createLiquidationSummaryFromSummary(liquidationDetails, timestamp) {
    return {
        action: 'Liquidation',
        liquidatedTokens: liquidationDetails.collateral_seized
            ? [
                {
                    symbol: extractTokenSymbol(liquidationDetails.collateral_seized.coin_type),
                    amountRaw: liquidationDetails.collateral_seized.token_amount,
                    coinType: liquidationDetails.collateral_seized.coin_type,
                    amountFormatted: formatAmount(liquidationDetails.collateral_seized.token_amount, liquidationDetails.collateral_seized.coin_type),
                },
            ]
            : [],
        collateralSeized: liquidationDetails.collateral_seized
            ? [
                {
                    symbol: extractTokenSymbol(liquidationDetails.collateral_seized.coin_type),
                    amountRaw: liquidationDetails.collateral_seized.token_amount,
                    coinType: liquidationDetails.collateral_seized.coin_type,
                    amountFormatted: formatAmount(liquidationDetails.collateral_seized.token_amount, liquidationDetails.collateral_seized.coin_type),
                },
            ]
            : undefined,
        debtRepaid: liquidationDetails.debt_repaid
            ? [
                {
                    symbol: extractTokenSymbol(liquidationDetails.debt_repaid.coin_type),
                    amountRaw: liquidationDetails.debt_repaid.token_amount,
                    coinType: liquidationDetails.debt_repaid.coin_type,
                    amountFormatted: formatAmount(liquidationDetails.debt_repaid.token_amount, liquidationDetails.debt_repaid.coin_type),
                },
            ]
            : undefined,
        protocolFee: liquidationDetails.protocol_fee
            ? [
                {
                    symbol: extractTokenSymbol(liquidationDetails.protocol_fee.coin_type),
                    amountRaw: liquidationDetails.protocol_fee.token_amount,
                    coinType: liquidationDetails.protocol_fee.coin_type,
                    amountFormatted: formatAmount(liquidationDetails.protocol_fee.token_amount, liquidationDetails.protocol_fee.coin_type),
                },
            ]
            : undefined,
        liquidatorBonus: liquidationDetails.liquidator_bonus
            ? [
                {
                    symbol: extractTokenSymbol(liquidationDetails.liquidator_bonus.coin_type),
                    amountRaw: liquidationDetails.liquidator_bonus.token_amount,
                    coinType: liquidationDetails.liquidator_bonus.coin_type,
                    amountFormatted: formatAmount(liquidationDetails.liquidator_bonus.token_amount, liquidationDetails.liquidator_bonus.coin_type),
                },
            ]
            : undefined,
        timestamp,
    };
}
// =====================
// Helper Functions
// =====================
function extractTokenSymbol(coinType) {
    const parts = coinType.split('::');
    return parts[parts.length - 1] || 'UNKNOWN';
}
function formatAmount(amount, coinTypeOrSymbol) {
    // Format based on token decimals from proper source
    // Try to get config by coinType first, then by symbol
    const tokenConfig = coinTypeOrSymbol.includes('::')
        ? getTokenConfig(coinTypeOrSymbol)
        : getTokenConfigBySymbol(coinTypeOrSymbol);
    const decimals = tokenConfig.decimals;
    const value = Number.parseFloat(amount) / Math.pow(10, decimals);
    // For very small amounts, show up to 9 decimal places
    // For larger amounts, show up to 6 decimal places
    if (value === 0) {
        return '0';
    }
    else if (Math.abs(value) < 0.000001) {
        return value.toLocaleString(undefined, { maximumFractionDigits: 9, minimumFractionDigits: 0 });
    }
    else {
        return value.toLocaleString(undefined, { maximumFractionDigits: 6, minimumFractionDigits: 0 });
    }
}
